import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
  IconButton,
  Divider,
  Checkbox,
  ListItemText,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import AddIcon from "@material-ui/icons/Add";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },
  formControl: {
    margin: theme.spacing(1, 0),
    minWidth: "100%",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  fieldContainer: {
    width: "100%",
  },
  switchRow: {
    display: "flex",
    justifyContent: "space-between",
  },
  windowsSection: {
    marginTop: theme.spacing(2),
  },
  windowRow: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    position: "relative",
  },
  windowRowFields: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
  dayChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },
  removeButton: {
    position: "absolute",
    top: 4,
    right: 4,
  },
  stepsSection: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  stepRow: {
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
  },
  stepOrderControls: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: 4,
  },
  stepOrderIndex: {
    fontSize: 11,
    color: "#888",
    fontWeight: 600,
  },
  stepBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  mediaPreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1),
    border: "1px solid #ddd",
    borderRadius: 4,
  },
  mediaThumb: {
    width: 48,
    height: 48,
    objectFit: "cover",
    borderRadius: 4,
  },
  mediaName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const DAY_OPTIONS = [
  { value: 1, label: "autoReplyFlowModal.form.windows.days.mon" },
  { value: 2, label: "autoReplyFlowModal.form.windows.days.tue" },
  { value: 3, label: "autoReplyFlowModal.form.windows.days.wed" },
  { value: 4, label: "autoReplyFlowModal.form.windows.days.thu" },
  { value: 5, label: "autoReplyFlowModal.form.windows.days.fri" },
  { value: 6, label: "autoReplyFlowModal.form.windows.days.sat" },
  { value: 0, label: "autoReplyFlowModal.form.windows.days.sun" },
];

const emptyWindow = {
  windowType: "weekly",
  daysOfWeek: "",
  specificDate: "",
  startTime: "",
  endTime: "",
  endDayOffset: 0,
};

const emptyStep = {
  type: "text",
  text: "",
  mediaPath: "",
  mediaMimetype: "",
  mediaOriginalName: "",
  expectedReply: "",
};

const STEP_TYPE_LABELS = {
  text: "autoReplyFlowModal.form.steps.typeText",
  image: "autoReplyFlowModal.form.steps.typeImage",
  video: "autoReplyFlowModal.form.steps.typeVideo",
  audio: "autoReplyFlowModal.form.steps.typeAudio",
  document: "autoReplyFlowModal.form.steps.typeDocument",
};

// Paso especial: no manda nada, pausa la secuencia hasta que el cliente
// responda algo que coincida con "Respuesta esperada" (igual lógica que
// "Palabras clave" de la regla, cualquiera de los valores separados por
// coma cuenta). No tiene traducción en i18n como los demás tipos porque es
// nuevo — se maneja aparte en vez de sumarlo a STEP_TYPE_LABELS.
const WAIT_REPLY_LABEL = "Esperar Respuesta";

const ACCEPT_BY_TYPE = {
  image: "image/*",
  video: "video/*",
  audio: "audio/*",
  document: "*/*",
};

const AutoReplyFlowSchema = Yup.object().shape({
  name: Yup.string().min(2, "Too Short!").required("Required"),
  matchType: Yup.string().oneOf(["keywords", "any"]),
  keywords: Yup.string().when("matchType", {
    is: (matchType) => matchType !== "any",
    then: Yup.string().required("Required"),
    otherwise: Yup.string().notRequired(),
  }),
  tagFilter: Yup.string().oneOf(["all", "with_tag", "without_tag"]),
  whatsappId: Yup.number().required("Required"),
  steps: Yup.array()
    .of(
      Yup.object().shape({
        type: Yup.string().oneOf([
          "text",
          "image",
          "video",
          "audio",
          "document",
          "wait_reply",
        ]),
        text: Yup.string().when("type", {
          is: "text",
          then: Yup.string().required("Required"),
          otherwise: Yup.string().notRequired(),
        }),
        mediaPath: Yup.string().when("type", {
          is: (type) => ["image", "video", "audio", "document"].includes(type),
          then: Yup.string().required("Required"),
          otherwise: Yup.string().notRequired(),
        }),
        expectedReply: Yup.string().when("type", {
          is: "wait_reply",
          then: Yup.string().required("Required"),
          otherwise: Yup.string().notRequired(),
        }),
      })
    )
    .min(1, "Agrega al menos un paso"),
  windows: Yup.array().of(
    Yup.object().shape({
      windowType: Yup.string().oneOf(["weekly", "specific_date"]).required(),
      daysOfWeek: Yup.string().when("windowType", {
        is: "weekly",
        then: Yup.string().required("Required"),
        otherwise: Yup.string().notRequired(),
      }),
      specificDate: Yup.string().when("windowType", {
        is: "specific_date",
        then: Yup.string().required("Required"),
        otherwise: Yup.string().notRequired(),
      }),
      startTime: Yup.string().when("windowType", {
        is: "weekly",
        then: Yup.string().required("Required"),
        otherwise: Yup.string().nullable(),
      }),
      endTime: Yup.string().when("windowType", {
        is: "weekly",
        then: Yup.string().required("Required"),
        otherwise: Yup.string().nullable(),
      }),
      endDayOffset: Yup.number().min(0).max(6),
    })
  ),
});

const AutoReplyFlowModal = ({ open, onClose, ruleId, onSave }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { whatsApps } = useContext(WhatsAppsContext);
  const [allRules, setAllRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const fileInputRefs = useRef({});

  const initialState = {
    name: "",
    keywords: "",
    matchType: "keywords",
    tagFilter: "all",
    active: true,
    priority: 0,
    whatsappId: "",
    windows: [],
    steps: [{ ...emptyStep }],
    parentRuleId: "",
    fireAfterAssigned: false,
    userScope: "all",
    userIds: [],
  };

  const [rule, setRule] = useState(initialState);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const { data } = await api.get("/autoReplyRules");
        if (isMounted.current) {
          setAllRules(data);
        }
      } catch (err) {
        toastError(err);
      }
    };
    if (open) {
      fetchRules();
    }
  }, [open]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users");
        if (isMounted.current) {
          setUsers(data.users || []);
        }
      } catch (err) {
        toastError(err);
      }
    };
    if (open) {
      fetchUsers();
    }
  }, [open]);

  useEffect(() => {
    const fetchRule = async () => {
      if (!ruleId) {
        setRule(initialState);
        return;
      }

      try {
        const { data } = await api.get(`/autoReplyRules/${ruleId}`);
        if (isMounted.current) {
          setRule({
            name: data.name,
            keywords: data.keywords,
            matchType: data.matchType || "keywords",
            tagFilter: data.tagFilter || "all",
            active: data.active,
            priority: data.priority,
            whatsappId: data.whatsappId,
            parentRuleId: data.parentRuleId || "",
            fireAfterAssigned: data.fireAfterAssigned || false,
            userScope: data.userScope || "all",
            userIds: (data.users || []).map((u) => u.id),
            windows: (data.windows || []).map((w) => ({
              windowType: w.windowType,
              daysOfWeek: w.daysOfWeek || "",
              specificDate: w.specificDate || "",
              startTime: w.startTime || "",
              endTime: w.endTime || "",
              endDayOffset: w.endDayOffset || 0,
            })),
            steps: (data.steps || []).length
              ? data.steps.map((s) => ({
                  type: s.type,
                  text: s.text || "",
                  mediaPath: s.mediaPath || "",
                  mediaMimetype: s.mediaMimetype || "",
                  mediaOriginalName: s.mediaOriginalName || "",
                  expectedReply: s.expectedReply || "",
                }))
              : [{ ...emptyStep }],
          });
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchRule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId, open]);

  const handleClose = () => {
    onClose();
    setRule(initialState);
  };

  const handleStepFileSelected = async (event, index, setFieldValue) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const formData = new FormData();
    formData.append("media", file);

    setUploadingIndex(index);
    try {
      const { data } = await api.post("/autoReplyRules/media", formData);
      setFieldValue(`steps[${index}].mediaPath`, data.mediaPath);
      setFieldValue(`steps[${index}].mediaMimetype`, data.mediaMimetype);
      setFieldValue(`steps[${index}].mediaOriginalName`, data.mediaOriginalName);
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingIndex(null);
    }
  };

  // Mismo criterio que en la regla simple: misma conexión, sin contarse a
  // sí misma ni a sus propias descendientes — la lista mezcla reglas
  // simples y avanzadas por igual, porque ambas viven en la misma tabla.
  const getParentRuleOptions = (whatsappId) => {
    if (!whatsappId) return [];

    const descendantIds = new Set();
    if (ruleId) {
      let frontier = [ruleId];
      while (frontier.length > 0) {
        const children = allRules.filter((r) => frontier.includes(r.parentRuleId));
        frontier = children.map((r) => r.id);
        frontier.forEach((id) => descendantIds.add(id));
      }
    }

    return allRules.filter(
      (r) =>
        r.whatsappId === whatsappId &&
        r.id !== ruleId &&
        !descendantIds.has(r.id)
    );
  };

  const handleSaveRule = async (values) => {
    const payload = {
      ...values,
      parentRuleId: values.parentRuleId || null,
      quickAnswerId: null,
    };
    try {
      if (ruleId) {
        await api.put(`/autoReplyRules/${ruleId}`, payload);
      } else {
        const { data } = await api.post("/autoReplyRules", payload);
        if (onSave) {
          onSave(data);
        }
      }
      toast.success(i18n.t("autoReplyFlowModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {ruleId
            ? i18n.t("autoReplyFlowModal.title.edit")
            : i18n.t("autoReplyFlowModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={rule}
          enableReinitialize={true}
          validationSchema={AutoReplyFlowSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveRule(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            setFieldValue,
          }) => (
            <Form>
              <DialogContent dividers>
                <Typography variant="caption" color="textSecondary">
                  {i18n.t("autoReplyFlowModal.helper")}
                </Typography>

                <div className={classes.fieldContainer} style={{ marginTop: 8 }}>
                  <Field
                    as={TextField}
                    label={i18n.t("autoReplyFlowModal.form.name")}
                    name="name"
                    autoFocus
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    fullWidth
                  />
                </div>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                  error={touched.whatsappId && Boolean(errors.whatsappId)}
                >
                  <InputLabel>
                    {i18n.t("autoReplyFlowModal.form.whatsapp")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("autoReplyFlowModal.form.whatsapp")}
                    name="whatsappId"
                  >
                    {whatsApps.map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        {whatsapp.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                >
                  <InputLabel>
                    {i18n.t("autoReplyFlowModal.form.matchType")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("autoReplyFlowModal.form.matchType")}
                    name="matchType"
                  >
                    <MenuItem value="keywords">
                      {i18n.t("autoReplyFlowModal.form.matchTypeKeywords")}
                    </MenuItem>
                    <MenuItem value="any">
                      {i18n.t("autoReplyFlowModal.form.matchTypeAny")}
                    </MenuItem>
                  </Field>
                </FormControl>
                {values.matchType !== "any" && (
                  <div className={classes.fieldContainer}>
                    <Field
                      as={TextField}
                      label={i18n.t("autoReplyFlowModal.form.keywords")}
                      name="keywords"
                      error={touched.keywords && Boolean(errors.keywords)}
                      helperText={
                        (touched.keywords && errors.keywords) ||
                        i18n.t("autoReplyFlowModal.form.keywordsHelper")
                      }
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                      fullWidth
                    />
                  </div>
                )}

                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                >
                  <InputLabel>
                    {i18n.t("autoReplyFlowModal.form.parentRule")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("autoReplyFlowModal.form.parentRule")}
                    name="parentRuleId"
                  >
                    <MenuItem value="">
                      {i18n.t("autoReplyFlowModal.form.parentRuleNone")}
                    </MenuItem>
                    {getParentRuleOptions(values.whatsappId).map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name}
                        {r.steps?.length ? " ⚡" : ""}
                      </MenuItem>
                    ))}
                  </Field>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("autoReplyFlowModal.form.parentRuleHelper")}
                  </Typography>
                </FormControl>

                <Divider className={classes.stepsSection} />
                <div className={classes.stepsSection}>
                  <Typography variant="subtitle2">
                    {i18n.t("autoReplyFlowModal.form.steps.title")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("autoReplyFlowModal.form.steps.helper")}
                  </Typography>

                  <FieldArray name="steps">
                    {({ push, remove, move }) => (
                      <>
                        {values.steps.length === 0 && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 8 }}
                          >
                            {i18n.t("autoReplyFlowModal.form.steps.empty")}
                          </Typography>
                        )}
                        {values.steps.map((step, index) => {
                          const rowErrors = (errors.steps && errors.steps[index]) || {};
                          const rowTouched = (touched.steps && touched.steps[index]) || {};

                          return (
                            <div className={classes.stepRow} key={index}>
                              <div className={classes.stepOrderControls}>
                                <IconButton
                                  size="small"
                                  disabled={index === 0}
                                  onClick={() => move(index, index - 1)}
                                >
                                  <KeyboardArrowUpIcon fontSize="small" />
                                </IconButton>
                                <span className={classes.stepOrderIndex}>
                                  {index + 1}
                                </span>
                                <IconButton
                                  size="small"
                                  disabled={index === values.steps.length - 1}
                                  onClick={() => move(index, index + 1)}
                                >
                                  <KeyboardArrowDownIcon fontSize="small" />
                                </IconButton>
                              </div>

                              <div className={classes.stepBody}>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 8,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <FormControl
                                    variant="outlined"
                                    margin="dense"
                                    style={{ minWidth: 200 }}
                                  >
                                    <InputLabel>
                                      {i18n.t("autoReplyFlowModal.form.steps.type")}
                                    </InputLabel>
                                    <Select
                                      label={i18n.t(
                                        "autoReplyFlowModal.form.steps.type"
                                      )}
                                      value={step.type}
                                      onChange={(e) => {
                                        setFieldValue(
                                          `steps[${index}].type`,
                                          e.target.value
                                        );
                                        setFieldValue(`steps[${index}].text`, "");
                                        setFieldValue(
                                          `steps[${index}].mediaPath`,
                                          ""
                                        );
                                        setFieldValue(
                                          `steps[${index}].mediaMimetype`,
                                          ""
                                        );
                                        setFieldValue(
                                          `steps[${index}].mediaOriginalName`,
                                          ""
                                        );
                                        setFieldValue(
                                          `steps[${index}].expectedReply`,
                                          ""
                                        );
                                      }}
                                    >
                                      {Object.keys(STEP_TYPE_LABELS).map((type) => (
                                        <MenuItem key={type} value={type}>
                                          {i18n.t(STEP_TYPE_LABELS[type])}
                                        </MenuItem>
                                      ))}
                                      <MenuItem value="wait_reply">
                                        {WAIT_REPLY_LABEL}
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                  <IconButton
                                    size="small"
                                    onClick={() => remove(index)}
                                    style={{ marginTop: 8 }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </div>

                                {step.type === "wait_reply" ? (
                                  <TextField
                                    label="Respuesta esperada"
                                    placeholder="Separadas por coma — cualquiera cuenta (ej. 1,uno,opción 1)"
                                    value={step.expectedReply}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `steps[${index}].expectedReply`,
                                        e.target.value
                                      )
                                    }
                                    error={
                                      rowTouched.expectedReply &&
                                      Boolean(rowErrors.expectedReply)
                                    }
                                    helperText={
                                      (rowTouched.expectedReply &&
                                        rowErrors.expectedReply) ||
                                      "La secuencia se pausa acá. Si el cliente responde con alguno de estos valores, sigue con el siguiente paso. Si no, se evalúan las automatizaciones normales."
                                    }
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                  />
                                ) : step.type === "text" ? (
                                  <TextField
                                    label={i18n.t(
                                      "autoReplyFlowModal.form.steps.typeText"
                                    )}
                                    placeholder={i18n.t(
                                      "autoReplyFlowModal.form.steps.textPlaceholder"
                                    )}
                                    value={step.text}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `steps[${index}].text`,
                                        e.target.value
                                      )
                                    }
                                    error={rowTouched.text && Boolean(rowErrors.text)}
                                    helperText={rowTouched.text && rowErrors.text}
                                    variant="outlined"
                                    margin="dense"
                                    multiline
                                    minRows={2}
                                    fullWidth
                                  />
                                ) : (
                                  <>
                                    <input
                                      ref={(el) => {
                                        fileInputRefs.current[index] = el;
                                      }}
                                      type="file"
                                      hidden
                                      accept={ACCEPT_BY_TYPE[step.type]}
                                      onChange={(e) =>
                                        handleStepFileSelected(e, index, setFieldValue)
                                      }
                                    />
                                    {step.mediaPath ? (
                                      <div className={classes.mediaPreview}>
                                        {step.mediaMimetype?.startsWith("image/") ? (
                                          <img
                                            alt={step.mediaOriginalName}
                                            src={`${getBackendUrl()}/public/${
                                              step.mediaPath
                                            }`}
                                            className={classes.mediaThumb}
                                          />
                                        ) : (
                                          <InsertDriveFileIcon />
                                        )}
                                        <Typography
                                          className={classes.mediaName}
                                          variant="body2"
                                        >
                                          {step.mediaOriginalName}
                                        </Typography>
                                        <Button
                                          size="small"
                                          disabled={uploadingIndex === index}
                                          onClick={() =>
                                            fileInputRefs.current[index]?.click()
                                          }
                                        >
                                          {i18n.t(
                                            "autoReplyFlowModal.form.steps.mediaChange"
                                          )}
                                        </Button>
                                      </div>
                                    ) : (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AttachFileIcon />}
                                        disabled={uploadingIndex === index}
                                        onClick={() =>
                                          fileInputRefs.current[index]?.click()
                                        }
                                      >
                                        {uploadingIndex === index
                                          ? i18n.t(
                                              "autoReplyFlowModal.form.steps.mediaUploading"
                                            )
                                          : i18n.t(
                                              "autoReplyFlowModal.form.steps.mediaUpload"
                                            )}
                                      </Button>
                                    )}
                                    {rowTouched.mediaPath && rowErrors.mediaPath && (
                                      <Typography variant="caption" color="error">
                                        {rowErrors.mediaPath}
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() => push({ ...emptyStep })}
                          style={{ marginTop: 8 }}
                        >
                          {i18n.t("autoReplyFlowModal.form.steps.add")}
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </div>

                <div className={classes.fieldContainer} style={{ marginTop: 16 }}>
                  <Field
                    as={TextField}
                    type="number"
                    label={i18n.t("autoReplyFlowModal.form.priority")}
                    name="priority"
                    helperText={i18n.t("autoReplyFlowModal.form.priorityHelper")}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                </div>
                <Divider className={classes.windowsSection} />
                <div className={classes.windowsSection}>
                  <Typography variant="subtitle2">
                    {i18n.t("autoReplyFlowModal.form.windows.title")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("autoReplyFlowModal.form.windows.helper")}
                  </Typography>
                  <FieldArray name="windows">
                    {({ push, remove }) => (
                      <>
                        {values.windows.map((window, index) => {
                          const rowErrors = (errors.windows && errors.windows[index]) || {};
                          const rowTouched = (touched.windows && touched.windows[index]) || {};
                          const selectedDays = window.daysOfWeek
                            ? window.daysOfWeek.split(",").map(Number)
                            : [];

                          const toggleDay = (day) => {
                            const newDays = selectedDays.includes(day)
                              ? selectedDays.filter((d) => d !== day)
                              : [...selectedDays, day];
                            setFieldValue(
                              `windows[${index}].daysOfWeek`,
                              newDays.join(",")
                            );
                          };

                          const isAllDay = !window.startTime && !window.endTime;

                          const toggleAllDay = () => {
                            if (isAllDay) {
                              setFieldValue(`windows[${index}].startTime`, "00:00");
                              setFieldValue(`windows[${index}].endTime`, "23:59");
                            } else {
                              setFieldValue(`windows[${index}].startTime`, "");
                              setFieldValue(`windows[${index}].endTime`, "");
                            }
                          };

                          return (
                            <div className={classes.windowRow} key={index}>
                              <IconButton
                                size="small"
                                className={classes.removeButton}
                                onClick={() => remove(index)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                style={{ minWidth: 220 }}
                              >
                                <InputLabel>
                                  {i18n.t("autoReplyFlowModal.form.windows.type")}
                                </InputLabel>
                                <Select
                                  label={i18n.t("autoReplyFlowModal.form.windows.type")}
                                  value={window.windowType}
                                  onChange={(e) =>
                                    setFieldValue(
                                      `windows[${index}].windowType`,
                                      e.target.value
                                    )
                                  }
                                >
                                  <MenuItem value="weekly">
                                    {i18n.t(
                                      "autoReplyFlowModal.form.windows.typeWeekly"
                                    )}
                                  </MenuItem>
                                  <MenuItem value="specific_date">
                                    {i18n.t(
                                      "autoReplyFlowModal.form.windows.typeDate"
                                    )}
                                  </MenuItem>
                                </Select>
                              </FormControl>

                              {window.windowType === "weekly" ? (
                                <>
                                  <div className={classes.dayChips}>
                                    {DAY_OPTIONS.map((day) => (
                                      <Chip
                                        key={day.value}
                                        label={i18n.t(day.label)}
                                        size="small"
                                        color={
                                          selectedDays.includes(day.value)
                                            ? "primary"
                                            : "default"
                                        }
                                        onClick={() => toggleDay(day.value)}
                                      />
                                    ))}
                                  </div>
                                  {rowTouched.daysOfWeek && rowErrors.daysOfWeek && (
                                    <Typography variant="caption" color="error">
                                      {rowErrors.daysOfWeek}
                                    </Typography>
                                  )}
                                  <div className={classes.windowRowFields}>
                                    <TextField
                                      label={i18n.t(
                                        "autoReplyFlowModal.form.windows.startTime"
                                      )}
                                      type="time"
                                      value={window.startTime}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `windows[${index}].startTime`,
                                          e.target.value
                                        )
                                      }
                                      variant="outlined"
                                      margin="dense"
                                      InputLabelProps={{ shrink: true }}
                                      error={
                                        rowTouched.startTime &&
                                        Boolean(rowErrors.startTime)
                                      }
                                    />
                                    <TextField
                                      label={i18n.t(
                                        "autoReplyFlowModal.form.windows.endTime"
                                      )}
                                      type="time"
                                      value={window.endTime}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `windows[${index}].endTime`,
                                          e.target.value
                                        )
                                      }
                                      variant="outlined"
                                      margin="dense"
                                      InputLabelProps={{ shrink: true }}
                                      error={
                                        rowTouched.endTime &&
                                        Boolean(rowErrors.endTime)
                                      }
                                    />
                                    <TextField
                                      label={i18n.t(
                                        "autoReplyFlowModal.form.windows.endDayOffset"
                                      )}
                                      type="number"
                                      inputProps={{ min: 0, max: 6 }}
                                      value={window.endDayOffset}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `windows[${index}].endDayOffset`,
                                          Number(e.target.value)
                                        )
                                      }
                                      variant="outlined"
                                      margin="dense"
                                      style={{ width: 160 }}
                                    />
                                  </div>
                                  <Typography variant="caption" color="textSecondary">
                                    {i18n.t(
                                      "autoReplyFlowModal.form.windows.endDayOffsetHelper"
                                    )}
                                  </Typography>
                                </>
                              ) : (
                                <div className={classes.windowRowFields}>
                                  <TextField
                                    label={i18n.t(
                                      "autoReplyFlowModal.form.windows.date"
                                    )}
                                    type="date"
                                    value={window.specificDate}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `windows[${index}].specificDate`,
                                        e.target.value
                                      )
                                    }
                                    variant="outlined"
                                    margin="dense"
                                    InputLabelProps={{ shrink: true }}
                                    error={
                                      rowTouched.specificDate &&
                                      Boolean(rowErrors.specificDate)
                                    }
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={isAllDay}
                                        onChange={toggleAllDay}
                                        color="primary"
                                      />
                                    }
                                    label={i18n.t(
                                      "autoReplyFlowModal.form.windows.allDay"
                                    )}
                                  />
                                  {!isAllDay && (
                                    <>
                                      <TextField
                                        label={i18n.t(
                                          "autoReplyFlowModal.form.windows.startTime"
                                        )}
                                        type="time"
                                        value={window.startTime}
                                        onChange={(e) =>
                                          setFieldValue(
                                            `windows[${index}].startTime`,
                                            e.target.value
                                          )
                                        }
                                        variant="outlined"
                                        margin="dense"
                                        InputLabelProps={{ shrink: true }}
                                      />
                                      <TextField
                                        label={i18n.t(
                                          "autoReplyFlowModal.form.windows.endTime"
                                        )}
                                        type="time"
                                        value={window.endTime}
                                        onChange={(e) =>
                                          setFieldValue(
                                            `windows[${index}].endTime`,
                                            e.target.value
                                          )
                                        }
                                        variant="outlined"
                                        margin="dense"
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => push({ ...emptyWindow })}
                          style={{ marginTop: 8 }}
                        >
                          {i18n.t("autoReplyFlowModal.form.windows.add")}
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </div>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                >
                  <InputLabel>
                    {i18n.t("autoReplyFlowModal.form.tagFilter")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("autoReplyFlowModal.form.tagFilter")}
                    name="tagFilter"
                  >
                    <MenuItem value="all">
                      {i18n.t("autoReplyFlowModal.form.tagFilterAll")}
                    </MenuItem>
                    <MenuItem value="with_tag">
                      {i18n.t("autoReplyFlowModal.form.tagFilterWithTag")}
                    </MenuItem>
                    <MenuItem value="without_tag">
                      {i18n.t("autoReplyFlowModal.form.tagFilterWithoutTag")}
                    </MenuItem>
                  </Field>
                </FormControl>
                <div className={classes.switchRow}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.active}
                        onChange={handleChange}
                        name="active"
                        color="primary"
                      />
                    }
                    label={i18n.t("autoReplyFlowModal.form.active")}
                  />
                </div>
                <div className={classes.switchRow}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.fireAfterAssigned}
                        onChange={handleChange}
                        name="fireAfterAssigned"
                        color="primary"
                      />
                    }
                    label="Disparar aunque el ticket ya tenga agente asignado"
                  />
                </div>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                >
                  <InputLabel>Agentes</InputLabel>
                  <Select
                    label="Agentes"
                    value={values.userScope}
                    onChange={(e) =>
                      setFieldValue("userScope", e.target.value)
                    }
                  >
                    <MenuItem value="all">Todos los agentes</MenuItem>
                    <MenuItem value="some">Agentes específicos</MenuItem>
                  </Select>
                  <Typography variant="caption" color="textSecondary">
                    Solo aplica una vez que el ticket ya tiene agente
                    asignado (ej. con "Disparar aunque el ticket ya tenga
                    agente asignado" activado arriba).
                  </Typography>
                </FormControl>
                {values.userScope === "some" && (
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    margin="dense"
                  >
                    <InputLabel>Agentes específicos</InputLabel>
                    <Select
                      multiple
                      label="Agentes específicos"
                      value={values.userIds}
                      onChange={(e) =>
                        setFieldValue("userIds", e.target.value)
                      }
                      renderValue={(selected) =>
                        users
                          .filter((u) => selected.includes(u.id))
                          .map((u) => u.name)
                          .join(", ")
                      }
                    >
                      {users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          <Checkbox
                            size="small"
                            checked={values.userIds.includes(u.id)}
                          />
                          <ListItemText primary={u.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("autoReplyFlowModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {ruleId
                    ? i18n.t("autoReplyFlowModal.buttons.okEdit")
                    : i18n.t("autoReplyFlowModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default AutoReplyFlowModal;
